import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LayoutDashboard, Users, Banknote, Settings as Cog, Megaphone, Crown, Radio, ShieldAlert, Trash2, Check, X, Loader2, Coins, DollarSign, Save } from "lucide-react";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "members", label: "Members", icon: Users },
  { key: "payouts", label: "Payouts", icon: Banknote },
  { key: "elite", label: "Elite", icon: Crown },
  { key: "banner", label: "Banner", icon: Megaphone },
  { key: "announcements", label: "Announce", icon: Radio },
  { key: "broadcast", label: "Broadcast", icon: Radio },
  { key: "settings", label: "Settings", icon: Cog },
];
const ROLES = ["member", "content_manager", "support_agent", "payout_manager", "moderator", "administrator", "owner"];

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");

  if (user && user.role === "member") {
    return <div className="glass rounded-2xl p-8 text-center"><ShieldAlert className="w-10 h-10 mx-auto text-jixxeo-danger mb-2" /><p className="font-bold">Access denied</p><p className="text-sm text-muted-foreground">This area is for staff only.</p></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2"><ShieldAlert className="w-6 h-6 text-jixxeo-gold" /><h2 className="font-head font-extrabold text-xl">Admin Console</h2><span className="text-[10px] uppercase tracking-widest text-jixxeo-gold border border-jixxeo-gold/40 rounded-full px-2 py-0.5">{user?.role}</span></div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {TABS.map((t) => { const I = t.icon; return (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
            className={`px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap flex items-center gap-1.5 ${tab === t.key ? "bg-jixxeo-green text-black" : "glass text-muted-foreground"}`}>
            <I className="w-3.5 h-3.5" />{t.label}
          </button>
        ); })}
      </div>
      {tab === "dashboard" && <Dashboard />}
      {tab === "members" && <Members />}
      {tab === "payouts" && <Payouts />}
      {tab === "elite" && <EliteList />}
      {tab === "banner" && <BannerEditor />}
      {tab === "announcements" && <Announcements />}
      {tab === "broadcast" && <Broadcast />}
      {tab === "settings" && <SettingsEditor />}
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!d) return <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-jixxeo-green" />;
  const stats = [
    ["Members", d.total_members], ["Active Today", d.active_today], ["New Signups", d.new_signups],
    ["Cleanups", d.total_cleanups], ["Collected", `${(d.total_weight_g / 1000).toFixed(1)}kg`], ["Pending Payouts", d.pending_payouts],
    ["Total Payouts", `$${d.total_payouts}`], ["Elite Subs", d.elite_subscribers], ["Sub Revenue", `$${d.subscription_revenue}`],
    ["Coin Revenue", `$${d.coin_revenue ?? 0}`], ["Ad Views", d.ad_views], ["Ad Revenue", `$${d.ad_revenue}`],
    ["Total Revenue", `$${(d.subscription_revenue + d.ad_revenue + (d.coin_revenue || 0)).toFixed(2)}`],
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-xl overflow-hidden">
        {stats.map(([l, v], i) => (
          <div key={i} className="bg-background p-4" data-testid={`stat-${l.toLowerCase().replace(/ /g, "-")}`}>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
            <p className="font-display text-xl mt-1 text-jixxeo-green">{v}</p>
          </div>
        ))}
      </div>
      <div>
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Live Activity</h3>
        <div className="space-y-1">
          {d.activity.map((a) => (
            <div key={a.id} className="flex items-center justify-between border-b border-white/5 py-2 text-sm">
              <span className="capitalize">{a.note || a.kind.replace(/_/g, " ")}</span>
              <span className="text-xs text-muted-foreground">{a.coins ? `${a.coins}🪙` : ""} {a.cash ? `$${a.cash}` : ""}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Members() {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);

  const load = async () => { try { const { data } = await api.get("/admin/members", { params: { q } }); setList(data); } catch (e) { toast.error(apiError(e)); } };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  return (
    <div className="space-y-3">
      <Input placeholder="Search members…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-member-search" className="bg-white/5 border-white/10 rounded-xl h-11" />
      <div className="space-y-1">
        {list.map((m) => (
          <button key={m.id} onClick={() => setSel(m)} data-testid={`admin-member-${m.id}`} className="w-full flex items-center justify-between border-b border-white/5 py-2.5 text-left hover:bg-white/5 px-2 rounded">
            <div className="min-w-0"><p className="font-bold text-sm truncate">{m.name} <span className="text-xs text-muted-foreground">@{m.username}</span></p>
              <p className="text-xs text-muted-foreground">{m.email} · {m.role}</p></div>
            <div className="text-right shrink-0">
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${m.status === "active" ? "bg-jixxeo-green/20 text-jixxeo-green" : m.status === "banned" ? "bg-jixxeo-danger/20 text-jixxeo-danger" : "bg-jixxeo-gold/20 text-jixxeo-gold"}`}>{m.status}</span>
              <p className="text-xs text-jixxeo-gold mt-0.5">{m.coins}🪙 ${m.cash_balance}</p>
            </div>
          </button>
        ))}
      </div>
      {sel && <MemberDialog member={sel} onClose={() => setSel(null)} onChange={(u) => { setSel(u); load(); }} />}
    </div>
  );
}

function MemberDialog({ member, onClose, onChange }) {
  const [coins, setCoins] = useState("");
  const [cash, setCash] = useState("");
  const [notifTitle, setNotifTitle] = useState("");
  const [notifBody, setNotifBody] = useState("");
  const [role, setRole] = useState(member.role);

  const act = async (fn, msg) => { try { const u = await fn(); if (u?.data) onChange(u.data); toast.success(msg); } catch (e) { toast.error(apiError(e)); } };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="glass border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{member.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{member.email} · {member.coins}🪙 · ${member.cash_balance} · {member.total_cleanups} cleanups · {member.verified_cleanups} verified</p>

        <div className="space-y-2 border-t border-white/10 pt-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Adjust Balance</p>
          <div className="flex gap-2">
            <Input type="number" placeholder="± Coins" value={coins} onChange={(e) => setCoins(e.target.value)} data-testid="adjust-coins" className="bg-white/5 border-white/10 rounded-lg h-10" />
            <Input type="number" placeholder="± Cash" value={cash} onChange={(e) => setCash(e.target.value)} data-testid="adjust-cash" className="bg-white/5 border-white/10 rounded-lg h-10" />
            <Button onClick={() => act(() => api.post(`/admin/members/${member.id}/adjust`, { coins: parseInt(coins || 0), cash: parseFloat(cash || 0) }), "Balance adjusted")} data-testid="adjust-apply" className="rounded-lg bg-jixxeo-green text-black font-bold shrink-0">Apply</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 border-t border-white/10 pt-3">
          <Button onClick={() => act(() => api.post(`/admin/members/${member.id}/status`, { status: member.status === "suspended" ? "active" : "suspended" }), "Status updated")} data-testid="suspend-btn" variant="ghost" className="glass border border-white/10 rounded-lg text-jixxeo-gold">{member.status === "suspended" ? "Unsuspend" : "Suspend"}</Button>
          <Button onClick={() => act(() => api.post(`/admin/members/${member.id}/status`, { status: member.status === "banned" ? "active" : "banned" }), "Status updated")} data-testid="ban-btn" variant="ghost" className="glass border border-white/10 rounded-lg text-jixxeo-danger">{member.status === "banned" ? "Unban" : "Ban"}</Button>
          <Button onClick={() => act(() => api.post(`/admin/members/${member.id}/verify`, { verified: !member.verified }), "Verification updated")} data-testid="verify-btn" variant="ghost" className="glass border border-white/10 rounded-lg text-jixxeo-green">{member.verified ? "Unverify" : "Verify"}</Button>
          <Button onClick={() => act(() => api.post(`/admin/members/${member.id}/badge`, { badge_id: "cleanups_100" }), "Badge assigned")} data-testid="badge-btn" variant="ghost" className="glass border border-white/10 rounded-lg">Give Badge</Button>
        </div>

        <div className="space-y-2 border-t border-white/10 pt-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Role</p>
          <div className="flex gap-2">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="bg-white/5 border-white/10 rounded-lg h-10" data-testid="role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={() => act(() => api.post(`/admin/members/${member.id}/role`, { role }), "Role updated")} data-testid="role-apply" className="rounded-lg bg-jixxeo-green text-black font-bold shrink-0">Set</Button>
          </div>
        </div>

        <div className="space-y-2 border-t border-white/10 pt-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Send Notification</p>
          <Input placeholder="Title" value={notifTitle} onChange={(e) => setNotifTitle(e.target.value)} data-testid="notif-title" className="bg-white/5 border-white/10 rounded-lg h-10" />
          <Textarea placeholder="Message" value={notifBody} onChange={(e) => setNotifBody(e.target.value)} data-testid="notif-body" className="bg-white/5 border-white/10 rounded-lg" />
          <Button onClick={() => { act(() => api.post(`/admin/members/${member.id}/notify`, { title: notifTitle, body: notifBody }), "Notification sent"); setNotifTitle(""); setNotifBody(""); }} data-testid="notif-send" className="w-full rounded-lg bg-jixxeo-blue text-white font-bold">Send</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Payouts() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = async () => { try { const { data } = await api.get("/admin/payouts", { params: { status } }); setList(data); } catch (e) { toast.error(apiError(e)); } };
  useEffect(() => { load(); }, [status]);
  const process = async (id, action) => { try { await api.post(`/admin/payouts/${id}/process`, { action }); toast.success(`Payout ${action}d`); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">{["pending", "approved", "denied", "all"].map((s) => (
        <button key={s} onClick={() => setStatus(s)} data-testid={`payout-filter-${s}`} className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize ${status === s ? "bg-jixxeo-green text-black" : "glass text-muted-foreground"}`}>{s}</button>
      ))}</div>
      {list.length === 0 ? <p className="text-center text-muted-foreground py-6 text-sm">No payouts.</p> : list.map((c) => (
        <div key={c.id} className="flex items-center justify-between border-b border-white/5 py-3" data-testid={`payout-${c.id}`}>
          <div><p className="font-bold text-sm">{c.user_name} — ${c.amount.toFixed(2)}</p><p className="text-xs text-muted-foreground">fee ${c.fee} · total ${c.total} · {new Date(c.created_at).toLocaleDateString()}</p></div>
          {c.status === "pending" ? (
            <div className="flex gap-2">
              <Button onClick={() => process(c.id, "approve")} data-testid={`approve-${c.id}`} size="sm" className="rounded-lg bg-jixxeo-green text-black font-bold h-8"><Check className="w-4 h-4" /></Button>
              <Button onClick={() => process(c.id, "deny")} data-testid={`deny-${c.id}`} size="sm" variant="ghost" className="rounded-lg glass border border-white/10 text-jixxeo-danger h-8"><X className="w-4 h-4" /></Button>
            </div>
          ) : <span className={`text-xs font-bold ${c.status === "approved" ? "text-jixxeo-green" : "text-jixxeo-danger"}`}>{c.status}</span>}
        </div>
      ))}
    </div>
  );
}

function EliteList() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/elite").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{list.length} Elite subscribers</p>
      {list.map((u) => (
        <div key={u.id} className="flex items-center justify-between border-b border-white/5 py-2.5" data-testid={`elite-${u.id}`}>
          <div><p className="font-bold text-sm flex items-center gap-1"><Crown className="w-3.5 h-3.5 text-jixxeo-purple" />{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></div>
          <span className="text-xs text-jixxeo-gold">{u.coins}🪙</span>
        </div>
      ))}
      {list.length === 0 && <p className="text-center text-muted-foreground py-6 text-sm">No Elite subscribers yet.</p>}
    </div>
  );
}

function BannerEditor() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/banner").then((r) => setB(r.data)); }, []);
  const save = async () => { try { await api.put("/admin/banner", b); toast.success("Banner updated — live on home"); } catch (e) { toast.error(apiError(e)); } };
  if (!b) return <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-jixxeo-green" />;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between glass rounded-xl p-3"><span className="text-sm font-bold">Banner enabled</span><Switch checked={b.enabled} onCheckedChange={(v) => setB({ ...b, enabled: v })} data-testid="banner-enabled" /></div>
      <Input placeholder="Title" value={b.title} onChange={(e) => setB({ ...b, title: e.target.value })} data-testid="banner-title" className="bg-white/5 border-white/10 rounded-xl h-11" />
      <Textarea placeholder="Message" value={b.message} onChange={(e) => setB({ ...b, message: e.target.value })} data-testid="banner-message" className="bg-white/5 border-white/10 rounded-xl" />
      <div className="flex gap-2">
        <Input placeholder="CTA text" value={b.cta_text} onChange={(e) => setB({ ...b, cta_text: e.target.value })} data-testid="banner-cta-text" className="bg-white/5 border-white/10 rounded-xl h-11" />
        <Input placeholder="CTA link (/scan)" value={b.cta_link} onChange={(e) => setB({ ...b, cta_link: e.target.value })} data-testid="banner-cta-link" className="bg-white/5 border-white/10 rounded-xl h-11" />
      </div>
      <Button onClick={save} data-testid="banner-save" className="w-full rounded-full bg-jixxeo-green text-black font-bold"><Save className="w-4 h-4 mr-2" />Save Banner</Button>
    </div>
  );
}

function Announcements() {
  const [list, setList] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const load = async () => { try { const { data } = await api.get("/admin/announcements"); setList(data); } catch (e) { toast.error(apiError(e)); } };
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/admin/announcements", { title, body, active: true }); setTitle(""); setBody(""); toast.success("Announcement posted"); load(); } catch (e) { toast.error(apiError(e)); } };
  const toggle = async (a) => { try { await api.put(`/admin/announcements/${a.id}`, { active: !a.active }); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { try { await api.delete(`/admin/announcements/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-3">
      <div className="glass rounded-xl p-3 space-y-2">
        <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ann-title" className="bg-white/5 border-white/10 rounded-lg h-10" />
        <Textarea placeholder="Message" value={body} onChange={(e) => setBody(e.target.value)} data-testid="ann-body" className="bg-white/5 border-white/10 rounded-lg" />
        <Button onClick={create} data-testid="ann-create" className="w-full rounded-lg bg-jixxeo-green text-black font-bold">Post Announcement</Button>
      </div>
      {list.map((a) => (
        <div key={a.id} className="flex items-center justify-between border-b border-white/5 py-2.5" data-testid={`ann-${a.id}`}>
          <div className="min-w-0"><p className="font-bold text-sm truncate">{a.title}</p><p className="text-xs text-muted-foreground truncate">{a.body}</p></div>
          <div className="flex items-center gap-2 shrink-0">
            <Switch checked={a.active} onCheckedChange={() => toggle(a)} data-testid={`ann-toggle-${a.id}`} />
            <button onClick={() => del(a.id)} data-testid={`ann-delete-${a.id}`} className="text-jixxeo-danger"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Broadcast() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => { setBusy(true); try { const { data } = await api.post("/admin/broadcast", { title, body }); toast.success(`Sent to ${data.sent} members`); setTitle(""); setBody(""); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  return (
    <div className="glass rounded-xl p-4 space-y-3">
      <p className="text-sm font-bold flex items-center gap-2"><Radio className="w-4 h-4 text-jixxeo-green" />Broadcast to all members</p>
      <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="broadcast-title" className="bg-white/5 border-white/10 rounded-lg h-10" />
      <Textarea placeholder="Message" value={body} onChange={(e) => setBody(e.target.value)} data-testid="broadcast-body" className="bg-white/5 border-white/10 rounded-lg" />
      <Button onClick={send} disabled={busy} data-testid="broadcast-send" className="w-full rounded-full bg-jixxeo-green text-black font-bold">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Broadcast"}</Button>
    </div>
  );
}

function SettingsEditor() {
  const [s, setS] = useState(null);
  const NUM = [
    ["cashout_min", "Cash-out Minimum ($)"], ["cashout_fee", "Processing Fee ($)"], ["coin_to_cash", "Coin→Cash Rate"],
    ["ad_reward_coins", "Ad Reward Coins"], ["ad_reward_cash", "Ad Reward Cash ($)"], ["daily_cash_cap", "Daily Cash Cap ($)"],
    ["daily_ad_limit", "Daily Ad Limit"], ["verified_cleanups_required", "Verified Cleanups Required"],
    ["checkin_coins", "Check-in Coins"], ["checkin_xp", "Check-in XP"], ["elite_price", "Elite Price ($)"], ["elite_monthly_coins", "Elite Monthly Coins"],
  ];
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const save = async () => { try { const p = { ...s }; delete p.banner; await api.put("/admin/settings", p); toast.success("Settings saved"); } catch (e) { toast.error(apiError(e)); } };
  if (!s) return <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-jixxeo-green" />;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {NUM.map(([k, label]) => (
          <div key={k}><label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</label>
            <Input type="number" step="any" value={s[k]} onChange={(e) => setS({ ...s, [k]: parseFloat(e.target.value) })} data-testid={`setting-${k}`} className="bg-white/5 border-white/10 rounded-lg h-10 mt-1" /></div>
        ))}
      </div>
      <div className="flex items-center justify-between glass rounded-xl p-3"><span className="text-sm font-bold text-jixxeo-danger">Maintenance Mode</span><Switch checked={s.maintenance_mode} onCheckedChange={(v) => setS({ ...s, maintenance_mode: v })} data-testid="setting-maintenance" /></div>
      <Button onClick={save} data-testid="settings-save" className="w-full rounded-full bg-jixxeo-green text-black font-bold"><Save className="w-4 h-4 mr-2" />Save Settings</Button>
    </div>
  );
}
